import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck, Circle } from 'lucide-react';

import { useApiData } from '../lib/hooks.js';
import { fmtDate, isoToday } from '../lib/format.js';
import { Card, Spinner, ErrorNote, Pill, SectionTitle, PageHeader, Button } from '../components/ui.jsx';

// The training tab: this week's three sessions, a way to look forward or back,
// and the phase map so the eight months have a visible shape.

export default function Train() {
  const [week, setWeek] = useState(null);
  const path = week ? `/training/plan?week=${week}` : '/training/plan';
  const { data, loading, error, reload } = useApiData(path, [week]);

  if (loading && !data) return <Spinner label="Loading the plan" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { plan, currentWeek, totalWeeks, phases } = data;
  const shown = plan.week;
  const today = isoToday();

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader
        title="Training"
        subtitle={`${plan.phase.name} · ${plan.phase.focus}`}
        right={
          <Link to="/safety" className="p-2 text-textMute hover:text-accent" aria-label="Training safety notes">
            <ShieldCheck size={20} />
          </Link>
        }
      />

      <div className="px-gutter">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setWeek(Math.max(1, shown - 1))}
            disabled={shown <= 1}
            className="p-2 -ml-2 text-textSoft disabled:opacity-30"
            aria-label="Previous week"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="text-center">
            <p className="text-card-title font-display">Week {shown}</p>
            <p className="text-meta text-textMute">
              {fmtDate(plan.weekStart, 'short')} – {fmtDate(plan.weekEnd, 'short')}
              {shown === currentWeek && ' · this week'}
            </p>
          </div>
          <button
            onClick={() => setWeek(Math.min(totalWeeks, shown + 1))}
            disabled={shown >= totalWeeks}
            className="p-2 -mr-2 text-textSoft disabled:opacity-30"
            aria-label="Next week"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {shown !== currentWeek && (
          <Button variant="ghost" size="sm" className="w-full mb-3" onClick={() => setWeek(currentWeek)}>
            Back to this week
          </Button>
        )}

        {plan.deload && (
          <Card className="mb-4 bg-accentSoft border-accent/40">
            <Pill tone="accent">Easier week</Pill>
            <p className="text-body-sm text-textSoft mt-2">
              One set fewer on everything and a shorter finisher, with the same weights. Planned in from the start — this is where the
              work you have done actually turns into progress.
            </p>
          </Card>
        )}

        <ul className="space-y-3 mb-6">
          {plan.sessions.map((s) => {
            const done = s.logged && (s.logged.status === 'done' || s.logged.status === 'shortened');
            return (
              <li key={s.slot}>
                <Link
                  to={`/train/${shown}/${s.slot}`}
                  className={`flex items-center gap-3 card hover:border-edgeHi transition-colors ${
                    done ? 'border-good/40' : s.date === today ? 'border-accent' : ''
                  }`}
                >
                  <div className="shrink-0">
                    {done ? <CheckCircle2 size={22} className="text-good" /> : <Circle size={22} className="text-textMute" strokeWidth={1.5} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-body font-semibold">{s.name}</p>
                    <p className="text-meta text-textMute">
                      {fmtDate(s.date, 'weekday')} · {s.focus}
                      {s.logged?.status === 'shortened' && ' · short version'}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-textMute shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>

        <SectionTitle>The eight months</SectionTitle>
        <div className="space-y-2 mb-6">
          {phases.map((p) => {
            const active = shown >= p.from && shown <= p.to;
            return (
              <Card key={p.id} className={active ? 'border-accent/50' : ''}>
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-card-title font-display">{p.name}</h3>
                  <span className="text-meta text-textMute">
                    Weeks {p.from}–{p.to}
                  </span>
                </div>
                <p className="text-body-sm text-textSoft">{active ? p.intro : p.focus}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
